import { Component, Input } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { AssetManagementService } from './asset-management-service';
import { InventoryTableService } from '@app/pages/asset-management/asset-management-table/inventory-table.service';
import { OffboardingRequestsTableService } from '@app/pages/asset-management/asset-management-table/offboarding-requests-table.service';
import { ToasterService } from '@app/shared/services/toaster.service';
import { AddCommentsComponent } from './cwr-offboardingCard/Offboarding-DetailsScreen/add-comments/add-comments.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-status-renderer',
  template: `
    <div class="status-container">
      <app-status-label
        [statusV]="statusValue"
        [showDropdown]="showDropdown"
        [category]="category"
        [readonly]="false"
        (statusChange)="onStatusChanged($event)"
      >
      </app-status-label>
    </div>
  `,
  styles: [
    `
      .status-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        height: 100%;
        padding: 4px;
        width: 100%;
        position: relative;
      }

      /* Override ag-Grid cell overflow */
      :host {
        overflow: visible !important;
      }

      .status-container ::ng-deep app-status-label {
        position: relative;
        z-index: 1000;
      }

      /* Make dropdown break out of cell boundaries */
      .status-container ::ng-deep .status-dropdown {
        position: relative;
      }

      .status-container ::ng-deep .status-dropdown ul.options {
        position: fixed !important;
        z-index: 9999 !important;
        max-height: 200px;
        overflow-y: auto;
        border-radius: 4px;
        background: #fff;
        list-style: none;
        margin: 0;
        padding: 4px 0;
        left: auto !important;
        right: auto !important;
        width: 180px !important;
        margin: -2px !important;
      }
      .status-container {
        width: 80% !important;
      }
      /* Ensure the dropdown appears above everything */
      .status-container ::ng-deep .status-dropdown.open ul.options {
        display: block !important;
      }
    `,
  ],
})
export class StatusRenderer implements ICellRendererAngularComp {
  params: any;
  statusValue: any = '';
  showDropdown = true;
  category: any;
  isDamageAnalytics = false;
  @Input() employeeId!: string;
  @Input() procuredOrderId!: string;

  constructor(
    private assetService: AssetManagementService,
    private toastrService: ToasterService,
    private inventoryTableService: InventoryTableService,
    private offboardingRequestsTableService: OffboardingRequestsTableService,
    private readonly dialog: MatDialog,
  ) {}

  agInit(params: any): void {
    this.isDamageAnalytics = params.isDamageAnalytics || false;
    if (this.isDamageAnalytics) {
      this.statusValue = params.value || params.data?.damageCostStatus || '';
      this.showDropdown = false;
    } else {
      this.statusValue = params.value || '';
      this.showDropdown =
        params.showDropdown !== undefined ? params.showDropdown : true;
    }
    this.category = params.category || 'Request';
    this.employeeId = params.data?.employeeId || params.data?.id || null;
    this.procuredOrderId = params.data?.procuredOrderId || null;
    this.params = params;
  }

  refresh(params: any): boolean {
    this.agInit(params);
    return true;
  }

  onStatusChanged(newStatus: string) {
    const previousStatus = this.statusValue;

    console.log(
      'Updating inventory order status for Procured Order ID:',
      this.procuredOrderId,
      'to new status:',
      newStatus,
    );

    const dialogRef = this.openDownloadConfirmDialog();

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        // User cancelled - force UI refresh to show previous status
        this.statusValue = '';
        setTimeout(() => {
          this.statusValue = previousStatus;
        }, 0);
        return;
      }
      if (this.category === 'Return' && this.employeeId) {
        this.statusValue = newStatus;
        this.assetService
          .updateReturnStatusOffboard({
            employeeId: String(this.employeeId),
            newReturnStatus: newStatus,
          })
          .subscribe({
            next: () => {
              if (
                this.params?.node?.setDataValue &&
                this.params?.colDef?.field
              ) {
                this.params.node.setDataValue(
                  this.params.colDef.field,
                  newStatus,
                );
              }
              this.toastrService.showSuccess(
                'Status updated successfully',
                'Success',
              );
              this.offboardingRequestsTableService.refreshData();
            },
            error: (err) => {
              this.statusValue = previousStatus;
              if (
                this.params?.node?.setDataValue &&
                this.params?.colDef?.field
              ) {
                this.params.node.setDataValue(
                  this.params.colDef.field,
                  previousStatus,
                );
              }
              const errorMsg = err?.error
                ? JSON.stringify(err.error)
                : err?.message || 'An error occurred';
              this.toastrService.showError(errorMsg, 'Error');
            },
          });
        return;
      } else if (this.category === 'Inventory' && this.procuredOrderId) {
        console.log(
          'Updating inventory order status for Procured Order ID:',
          this.procuredOrderId,
          'to new status:',
          newStatus,
        );
        if (previousStatus === 'Delivered' && newStatus === 'Pending') {
          this.toastrService.showError(
            'Cannot change status from Delivered to Pending',
            'Error',
          );
          this.inventoryTableService.loadInitialData(
            'inventory-order',
            this.params.data.location,
          );
          return;
        }

        this.assetService
          .updateInventoryOrderStatus(
            this.params.data.procuredOrderId,
            newStatus,
          )
          .subscribe({
            next: () => {
              this.statusValue = newStatus;

              if (
                this.params?.node?.setDataValue &&
                this.params?.colDef?.field
              ) {
                this.params.node.setDataValue(
                  this.params.colDef.field,
                  newStatus,
                );
              }

              this.inventoryTableService.loadInitialData(
                'inventory-order',
                this.params.data.location,
              );
              this.toastrService.showSuccess(
                'Delivery status successfully updated',
                'Success',
              );
            },
            error: () => {
              this.statusValue = previousStatus;
              if (
                this.params?.node?.setDataValue &&
                this.params?.colDef?.field
              ) {
                this.params.node.setDataValue(
                  this.params.colDef.field,
                  previousStatus,
                );
              }
              this.toastrService.showError(
                'Cannot update delivery status',
                'Error',
              );
            },
          });
        return;
      }
    });
  }

  openDownloadConfirmDialog() {
    const dialogRef = this.dialog.open(AddCommentsComponent, {
      data: {
        option: 'confirmation',
        content: 'Are you sure you want to change the status?',
      },
    });

    return dialogRef;
  }
}
